import { useMemo } from 'react';

import { fromPromise } from 'xstate';

import { OrderTypeValues } from '@sg/shared-be-features-ordering';

import {
  STRIPE_NETWORK_ERROR,
  StripeLogger,
  usePaymentStateExistence,
} from '@order/integrations-stripe';
import { useTelemetry } from '@order/Telemetry';

import { useCreateSweetpassPaymentIntent } from '../../../../hooks/useCreateSweetpassPaymentIntent';
import {
  type ConfirmPaymentInput,
  type ConfirmPaymentResponse,
  type StripeConfirmHandler,
} from '../../setup/sweetpass-checkout.machine.types';
import { toErrorMessage } from '../../setup/sweetpass-checkout.machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Confirms the Stripe payment element for a sweetpass purchase.
 */
export const useConfirmSweetpassPaymentActor = () => {
  const createSweetpassPaymentIntent = useCreateSweetpassPaymentIntent();
  const checkPaymentExists = usePaymentStateExistence();
  const { track } = useTelemetry();

  const confirmPayment = useMemo(
    () =>
      fromPromise<ConfirmPaymentResponse, ConfirmPaymentInput>(async ({ input }) => {
        const { sweetpassId, amount, confirmStripe, stripeConfirmHandlerRef } = input;
        let intent: PaymentIntentResult | undefined;

        stripeConfirmHandlerRef.current = async (
          paymentMethod,
          shouldSetupFutureUsage,
          callback,
        ) => {
          try {
            intent = await createSweetpassPaymentIntent({
              sweetpassId,
              amount,
              paymentMethodId: paymentMethod.id,
              shouldSetupFutureUsage,
            });
          } catch (error) {
            callback(toIntentCreationFailure(toErrorMessage(error)));
            return;
          }

          if (intent.result === 'already-purchased') {
            callback(toIntentCreationFailure(intent.errorMessage));
            return;
          }

          callback({ clientSecret: intent.clientSecret });
        };

        const trackStripeUsage = (payload: Record<string, unknown>) => {
          track('sweetpass.stripe-usage', { sweetpassId, ...payload });
        };

        try {
          const result = await confirmStripe();

          if (intent?.result === 'already-purchased') {
            return { result: 'already-purchased', errorMessage: intent.errorMessage };
          }

          if (result.status === 'canceled') {
            StripeLogger.logCancelation();
            trackStripeUsage({ event: 'result_canceled' });
            return { result: 'canceled' };
          }

          if (result.status === 'failed') {
            const errorMessage = result.error.message;
            StripeLogger.logConfirmError(errorMessage);
            trackStripeUsage({ event: 'result_failed', error: errorMessage });

            const recoveredPayment = await recoverConfirmedPayment({
              errorMessage,
              intent,
              checkPaymentExists,
              trackStripeUsage,
            });

            return recoveredPayment || { result: 'failed', errorMessage };
          }

          StripeLogger.logConfirmResult(result);
          trackStripeUsage({ event: 'result_completed', result });

          if (intent?.result !== 'created') {
            return { result: 'failed', errorMessage: MISSING_INTENT_MESSAGE };
          }

          return {
            result: 'confirmed',
            orderId: intent.orderId,
            paymentIntentId: intent.paymentIntentId,
          };
        } finally {
          stripeConfirmHandlerRef.current = noopConfirmHandler;
        }
      }),
    [createSweetpassPaymentIntent, checkPaymentExists, track],
  );

  return { confirmPayment };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A lost connection doesn't mean the payment failed, so it's treated as confirmed
 * when its payment state exists, leaving its outcome to the payment state polling.
 */
const recoverConfirmedPayment = async (
  params: RecoverConfirmedPaymentParams,
): Promise<ConfirmedPayment | undefined> => {
  const { errorMessage, intent, checkPaymentExists, trackStripeUsage } = params;

  if (errorMessage !== STRIPE_NETWORK_ERROR || intent?.result !== 'created') return undefined;

  const { orderId } = intent;
  const paymentIntentId = await checkPaymentExists(orderId, OrderTypeValues.SweetpassOrder).catch(
    () => undefined,
  );

  if (!paymentIntentId) return undefined;

  trackStripeUsage({ event: 'result_failed_with_payment_state', error: errorMessage });

  return { result: 'confirmed', orderId, paymentIntentId };
};

const toIntentCreationFailure = (message = INTENT_CREATION_FAILURE_MESSAGE) => {
  return { error: { code: 'Failed' as const, message } };
};

/**
 * The confirm handler is only meaningful while a purchase is being confirmed.
 */
export const noopConfirmHandler: StripeConfirmHandler = () => {};

// ─── Constants ───────────────────────────────────────────────────────────────

const INTENT_CREATION_FAILURE_MESSAGE = 'Unable to create the sweetpass payment intent';
const MISSING_INTENT_MESSAGE = 'Stripe completed without a sweetpass payment intent';

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentIntentResult = Awaited<ReturnType<ReturnType<typeof useCreateSweetpassPaymentIntent>>>; // prettier-ignore
type ConfirmedPayment = Extract<ConfirmPaymentResponse, { result: 'confirmed' }>;

type RecoverConfirmedPaymentParams = {
  errorMessage: string;
  intent: PaymentIntentResult | undefined;
  checkPaymentExists: ReturnType<typeof usePaymentStateExistence>;
  trackStripeUsage: (payload: Record<string, unknown>) => void;
};
